import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authcontext';
import { getLicenseStatus } from '../services/api';
import styles from './PaymentStatusBanner.module.css';

function PaymentStatusBanner() {
  const { user } = useAuth();
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkLicenseStatus = async () => {
      if (!user?.uid) return;
      
      try {
        const status = await getLicenseStatus(user.uid);
        setLicenseStatus(status);
        
        // Check if user has already dismissed this specific warning
        const dismissKey = `payment_warning_dismissed_${status.status}_${status.gracePeriodEnd}`;
        const isDismissed = localStorage.getItem(dismissKey) === 'true';
        setDismissed(isDismissed);
        
      } catch (error) {
        console.error('PaymentStatusBanner: Error checking license status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkLicenseStatus();
  }, [user]);

  const handleDismiss = () => {
    if (licenseStatus?.inGracePeriod) {
      const dismissKey = `payment_warning_dismissed_${licenseStatus.status}_${licenseStatus.gracePeriodEnd}`;
      localStorage.setItem(dismissKey, 'true');
    }
    setDismissed(true);
  };

  const formatTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffMs = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 1) {
      return `${diffDays} days`;
    } else if (diffHours > 1) {
      return `${diffHours} hours`;
    } else {
      return 'less than 1 hour';
    }
  };

  // Don't show banner if loading, dismissed, or no issues
  if (loading || dismissed || !licenseStatus || (!licenseStatus.inGracePeriod && licenseStatus.valid)) {
    return null;
  }

  // Account suspended
  if (!licenseStatus.valid) {
    return (
      <div className={`${styles.banner} ${styles.critical}`}>
        <div className={styles.content}>
          <div className={styles.icon}>🚨</div>
          <div className={styles.message}>
            <div className={styles.title}>Account Suspended</div>
            <div className={styles.description}>
              Your account has been suspended due to payment issues. 
              Please resolve your payment method to regain access to your data.
            </div>
          </div>
          <div className={styles.actions}>
            <a href="/account-settings" className={styles.actionButton}>
              Update Payment
            </a>
          </div>
        </div>
      </div>
    );
  }

  // In grace period
  if (licenseStatus.inGracePeriod) {
    const timeRemaining = formatTimeRemaining(licenseStatus.gracePeriodEnd);
    const isCritical = timeRemaining.includes('hour') || timeRemaining === '1 days';
    
    return (
      <div className={`${styles.banner} ${isCritical ? styles.critical : styles.warning}`}>
        <div className={styles.content}>
          <div className={styles.icon}>{isCritical ? '🚨' : '⚠️'}</div>
          <div className={styles.message}>
            <div className={styles.title}>
              {isCritical ? 'URGENT: Payment Issue' : 'Payment Issue'}
            </div>
            <div className={styles.description}>
              Your payment failed and you have {timeRemaining} remaining before your account is suspended. 
              {licenseStatus.paymentFailureCount > 1 && 
                ` This is attempt #${licenseStatus.paymentFailureCount}.`
              }
            </div>
          </div>
          <div className={styles.actions}>
            <a href="/account-settings" className={styles.actionButton}>
              Fix Payment
            </a>
            <button onClick={handleDismiss} className={styles.dismissButton}>
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default PaymentStatusBanner;

